// ORBAT-creator: bygg og vedlikehold hele styrkehierarkiet (FOH → forsvarsgren →
// … → lag). Split-screen: venstre = milsymbol-tre (sammenleggbart, søkbart,
// dra-slipp for reparent), høyre = detaljpanel (navn, echelon, SIDC med live
// milsymbol-forhåndsvisning, HQ, delegert sjef, notat). Tastatur-først.
//
// Motoren er ren og delt (@skade/contracts/orbat): hver redigering er en
// OrbatEvent som anvendes via applyOrbatEvent, persisteres (localStorage) og kan
// deles over collab. RBAC: superadmin/FOH ser & redigerer HELE treet; en sjef
// redigerer kun sitt eget subtre (eget distrikt eller delegert kommando). Alt
// annet vises skrivebeskyttet (hengelås). All feltavledet tekst via textContent.
import ms from 'milsymbol';
import {
  buildOrbatTree, applyOrbatEvent, withOrbatPaths, orbatChildEchelon,
  ORBAT_ECHELON_ORDER, ORBAT_ECHELON_LABEL, ORBAT_ECHELON_ABBR, ORBAT_ECHELON_SIDC,
  ORBAT_SEED, gnaUserIdFromPhone, normalizeMatrixId,
  type OrbatUnit, type OrbatNode, type OrbatEvent, type OrbatEchelon,
} from '@skade/contracts';
import {
  ROLE_RANK, orgFraOrbatLinje, foreslattRolleForEchelon,
  type User, type Role, type OrgUnit,
} from '@skade/rbac';
import { bekreft } from './shell.js';

export interface OrbatCreatorHooks {
  user: User;
  /** Del en hendelse videre (collab). Valgfritt. */
  onEvent?: (ev: OrbatEvent) => void;
  /** Åpne på egen skjerm (docked-modus). */
  onPopout?: () => void;
  /** Frittstående vindu (fyller skjermen, ingen lukk/pop-out). */
  embedded?: boolean;
  /** Hjemmeserver-domene — telefonnummer i personfeltene utledes til GNÅ-id. */
  defaultDomain?: string;
  /** Utsted enrolment-nøkkel via hubben (RBAC-håndhevet der). */
  onIssueKey?: (role: Role, org: OrgUnit) => Promise<{ code: string; expiresAt: number } | { error: string }>;
  /** Send nøkkel-meldingen til personen via GNÅ 1:1 (Feltveksleren). */
  onSendKey?: (matrixUserId: string, navn: string | undefined, text: string) => void;
}

const STORE_KEY = 'skade.orbat.v1';

/** Default-SIDC for en ny enhet: venn, bakke, infanteri, med riktig echelon. */
function defaultSidc(ech: OrbatEchelon): string {
  return ('SFGP' + 'UCI---' + '-' + ORBAT_ECHELON_SIDC[ech] + '---').slice(0, 15);
}

/** milsymbol-SVG fra SIDC (trygt: generert av milsymbol, ikke feltdata). */
function symbolSvg(sidc: string | undefined, size: number): string {
  try {
    return new ms.Symbol(sidc || 'SFGP-----------', { size, standard: 'APP6', outlineWidth: 2 }).asSVG();
  } catch {
    return '';
  }
}

export class OrbatCreator {
  readonly el: HTMLElement;
  private open = false;
  private units: OrbatUnit[] = [];
  private selectedId: string | null = null;
  private collapsed = new Set<string>();
  private filter = '';
  private scopeMine = false;
  private flatIds: string[] = []; // synlige rader i rekkefølge (tastaturnavigasjon)

  private treeEl!: HTMLElement;
  private detailEl!: HTMLElement;
  private searchEl!: HTMLInputElement;
  private scopeBtn!: HTMLButtonElement;

  constructor(private hooks: OrbatCreatorHooks) {
    this.units = this.load();
    this.el = this.build();
    if (hooks.embedded) { this.open = true; this.el.classList.add('open', 'embedded'); }
    this.render();
  }

  toggle() {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open) { this.render(); this.treeEl.focus(); }
  }

  /**
   * Styrkestrukturen slik den er NÅ — kilden ORBAT-velgeren på kartet leser fra.
   *
   * Den må være live og ikke `ORBAT_SEED`: har operatøren opprettet et lag i
   * ORBAT-panelet, skal laget kunne plasseres på kartet i samme økt. Leser man
   * seeden i stedet, kan man bare plassere enheter ingen har rørt.
   * Kopi ut, slik at kartlaget ikke kan mutere strukturen bak hendelseslogen.
   */
  enheter(): OrbatUnit[] {
    return this.units.map((u) => ({ ...u }));
  }

  // ── Persistens ─────────────────────────────────────────────────────────────
  private load(): OrbatUnit[] {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length) return withOrbatPaths(arr as OrbatUnit[]);
      }
    } catch { /* korrupt cache → seed */ }
    return ORBAT_SEED.map((u) => ({ ...u }));
  }
  private persist() {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(this.units)); } catch { /* full */ }
  }

  /** Ta imot en hendelse fra collab (annen operatør). */
  ingestEvent(ev: OrbatEvent) {
    this.units = withOrbatPaths(applyOrbatEvent(this.units, ev));
    this.persist();
    if (this.open) this.render();
  }

  // ── Mutasjon (lokal) ─────────────────────────────────────────────────────
  private emit(ev: OrbatEvent) {
    this.units = withOrbatPaths(applyOrbatEvent(this.units, ev));
    this.persist();
    this.hooks.onEvent?.(ev);
    this.render();
  }

  // ── RBAC ────────────────────────────────────────────────────────────────
  private canEditAll(): boolean {
    return this.hooks.user.role === 'foh';
  }
  /** Rot-noder brukeren fører kommando over (delegert eller eget distrikt). */
  private editRootIds(): string[] {
    const uid = this.hooks.user.id;
    const roots = this.units.filter((u) => u.commanderId === uid).map((u) => u.id);
    const dist = this.hooks.user.org.district;
    if (dist && this.units.some((u) => u.id === dist) && !roots.includes(dist)) roots.push(dist);
    return roots;
  }
  /** Alle enhets-id-er brukeren kan redigere (rot-subtrærne). */
  private editableIds(): Set<string> {
    const ids = new Set<string>();
    if (this.canEditAll()) { for (const u of this.units) ids.add(u.id); return ids; }
    const childrenOf = new Map<string, string[]>();
    for (const u of this.units) if (u.parentId) {
      const arr = childrenOf.get(u.parentId) ?? [];
      arr.push(u.id); childrenOf.set(u.parentId, arr);
    }
    const stack = [...this.editRootIds()];
    while (stack.length) {
      const id = stack.pop()!;
      if (ids.has(id)) continue;
      ids.add(id);
      for (const c of childrenOf.get(id) ?? []) stack.push(c);
    }
    return ids;
  }

  // ── DOM ───────────────────────────────────────────────────────────────────
  private build(): HTMLElement {
    const p = div('report-composer orbat-creator');
    // Hode
    const head = div('report-head');
    head.append(span('report-title', 'ORBAT-creator — styrkehierarki'));
    if (!this.hooks.embedded) {
      if (this.hooks.onPopout) {
        const po = btn('report-x', '⧉', 'Åpne ORBAT på egen skjerm');
        po.style.marginLeft = 'auto';
        po.onclick = () => this.hooks.onPopout?.();
        head.append(po);
      }
      const x = btn('report-x', '×', 'Lukk');
      if (!this.hooks.onPopout) x.style.marginLeft = 'auto';
      x.onclick = () => this.toggle();
      head.append(x);
    }
    p.append(head);

    // Verktøylinje: søk + scope-bytte + rolle
    const bar = div('orbat-c-bar');
    this.searchEl = document.createElement('input');
    this.searchEl.className = 'orbat-c-search';
    this.searchEl.placeholder = 'Søk i treet …';
    this.searchEl.oninput = () => { this.filter = this.searchEl.value.trim().toLowerCase(); this.render(); };
    bar.append(this.searchEl);
    this.scopeBtn = btn('orbat-c-scope', '', '');
    this.scopeBtn.onclick = () => { this.scopeMine = !this.scopeMine; this.render(); };
    bar.append(this.scopeBtn);
    p.append(bar);

    // Hint (tastatur)
    const hint = span('orbat-c-hint',
      'Ctrl+Enter: under-enhet · Shift+Enter: søsken · F2: navn · Delete: slett · piltaster: naviger · dra: flytt');
    p.append(hint);

    // Split
    const split = div('orbat-c-split');
    this.treeEl = div('orbat-c-tree');
    this.treeEl.tabIndex = 0;
    this.treeEl.onkeydown = (e) => this.onKey(e);
    this.detailEl = div('orbat-c-detail');
    split.append(this.treeEl, this.detailEl);
    p.append(split);
    return p;
  }

  // ── Rendering ──────────────────────────────────────────────────────────────
  private roots(): OrbatNode[] {
    const all = buildOrbatTree(this.units);
    if (this.scopeMine && !this.canEditAll()) {
      const rootIds = new Set(this.editRootIds());
      const found: OrbatNode[] = [];
      const walk = (n: OrbatNode) => {
        if (rootIds.has(n.unit.id)) { found.push(n); return; }
        n.children.forEach(walk);
      };
      all.forEach(walk);
      return found;
    }
    return all;
  }

  /** Id-er som skal vises gitt søket: treff + alle deres forfedre. null = alle. */
  private searchVisible(): Set<string> | null {
    if (!this.filter) return null;
    const byId = new Map(this.units.map((u) => [u.id, u]));
    const vis = new Set<string>();
    for (const u of this.units) {
      if (!u.name.toLowerCase().includes(this.filter)) continue;
      let cur: OrbatUnit | undefined = u;
      while (cur && !vis.has(cur.id)) {
        vis.add(cur.id);
        cur = cur.parentId ? byId.get(cur.parentId) : undefined;
      }
    }
    return vis;
  }

  render() {
    if (!this.open && !this.hooks.embedded) return;
    // Scope-knapp tekst
    if (this.canEditAll()) {
      this.scopeBtn.textContent = 'FOH — hele treet';
      this.scopeBtn.disabled = true;
    } else {
      this.scopeBtn.disabled = false;
      this.scopeBtn.textContent = this.scopeMine ? 'Viser: min enhet' : 'Viser: hele treet';
    }

    const editable = this.editableIds();
    const searchVis = this.searchVisible();
    const roots = this.roots();

    // Flat liste av synlige rader (respekterer collapsed, med mindre søk aktivt).
    this.flatIds = [];
    this.treeEl.innerHTML = '';
    const rows: HTMLElement[] = [];
    const walk = (n: OrbatNode, depth: number) => {
      if (searchVis && !searchVis.has(n.unit.id)) return;
      this.flatIds.push(n.unit.id);
      rows.push(this.rowFor(n, depth, editable.has(n.unit.id)));
      const expanded = searchVis ? true : !this.collapsed.has(n.unit.id);
      if (expanded) for (const c of n.children) walk(c, depth + 1);
    };
    for (const r of roots) walk(r, 0);

    if (!rows.length) {
      this.treeEl.append(span('orbat-c-empty', 'Ingen enheter i din scope.'));
    } else {
      for (const r of rows) this.treeEl.append(r);
    }

    // Behold/rydd valg
    if (this.selectedId && !this.flatIds.includes(this.selectedId)) {
      this.selectedId = null;
    }
    this.renderDetail(editable);
  }

  private rowFor(n: OrbatNode, depth: number, canEdit: boolean): HTMLElement {
    const u = n.unit;
    const row = div('orbat-c-row');
    if (u.id === this.selectedId) row.classList.add('sel');
    row.style.paddingLeft = `${4 + depth * 15}px`;
    row.draggable = canEdit;

    // Sammenleggbar-toggle
    const tog = span('orbat-c-tog', n.children.length ? (this.collapsed.has(u.id) ? '▸' : '▾') : '');
    if (n.children.length) tog.onclick = (e) => { e.stopPropagation(); this.toggleCollapse(u.id); };
    row.append(tog);

    // milsymbol-ikon
    const icon = span('orbat-c-icon', '');
    icon.innerHTML = symbolSvg(u.sidc, 22);
    row.append(icon);

    row.append(span('orbat-c-ech', ORBAT_ECHELON_ABBR[u.echelon]));
    row.append(span('orbat-c-name', u.name));
    if (u.commanderId) row.append(span('orbat-c-cmd', '★'));
    if (!canEdit) { const l = span('orbat-c-lock', '🔒'); l.title = 'Utenfor din redigeringsscope'; row.append(l); }

    row.onclick = () => this.select(u.id);
    // Dra-slipp for reparent (OPCON/TACON)
    row.ondragstart = (e) => { e.dataTransfer?.setData('text/orbat', u.id); };
    row.ondragover = (e) => { if (canEdit) { e.preventDefault(); row.classList.add('drop'); } };
    row.ondragleave = () => row.classList.remove('drop');
    row.ondrop = (e) => {
      row.classList.remove('drop');
      const src = e.dataTransfer?.getData('text/orbat');
      if (src && src !== u.id) this.reparent(src, u.id);
    };
    return row;
  }

  private renderDetail(editable: Set<string>) {
    this.detailEl.innerHTML = '';
    const u = this.selectedId ? this.units.find((x) => x.id === this.selectedId) : null;
    if (!u) {
      this.detailEl.append(span('orbat-c-empty', 'Velg en enhet i treet for å redigere.'));
      return;
    }
    const canEdit = editable.has(u.id);

    // Stor forhåndsvisning
    const prev = div('orbat-c-preview');
    const draw = (sidc: string) => { prev.innerHTML = symbolSvg(sidc, 64); };
    draw(u.sidc ?? '');
    this.detailEl.append(prev);
    this.detailEl.append(span('orbat-c-path', u.path ?? u.id));
    if (!canEdit) this.detailEl.append(span('orbat-c-ro', '🔒 Skrivebeskyttet — utenfor din kommando.'));

    const field = (label: string, input: HTMLElement) => {
      const w = div('orbat-c-field');
      w.append(span('orbat-c-label', label), input);
      this.detailEl.append(w);
    };

    // Navn
    const name = textInput(u.name, canEdit);
    name.className += ' orbat-c-nameinput';
    name.onchange = () => this.update(u.id, { name: name.value.trim() || u.name });
    field('Navn', name);

    // Echelon
    const ech = document.createElement('select');
    ech.className = 'orbat-c-input'; ech.disabled = !canEdit;
    for (const e of ORBAT_ECHELON_ORDER) {
      const o = document.createElement('option');
      o.value = e; o.textContent = ORBAT_ECHELON_LABEL[e];
      if (e === u.echelon) o.selected = true;
      ech.append(o);
    }
    ech.onchange = () => this.update(u.id, { echelon: ech.value as OrbatEchelon });
    field('Echelon', ech);

    // SIDC + live-forhåndsvisning
    const sidc = textInput(u.sidc ?? '', canEdit);
    sidc.placeholder = 'SIDC (APP-6/2525)';
    sidc.oninput = () => draw(sidc.value.trim());
    sidc.onchange = () => this.update(u.id, { sidc: sidc.value.trim() });
    field('SIDC', sidc);

    // HQ
    const hq = textInput(u.hq ?? '', canEdit);
    hq.onchange = () => this.update(u.id, { hq: hq.value.trim() });
    field('Standkvarter (HQ)', hq);

    // Delegert sjef — tåler telefonnummer OG @id (samme utledningsregel som
    // Kontakter/Feltveksler: «91994407» → @gna4791994407:<domene>).
    const cmd = textInput(u.commanderId ?? '', canEdit);
    cmd.placeholder = 'Telefonnummer eller @bruker:domene';
    cmd.onchange = () => {
      const id = this.personId(cmd.value);
      cmd.value = id ?? cmd.value;
      this.emit({ op: 'delegate', id: u.id, commanderId: id || undefined });
    };
    field('Delegert sjef', cmd);
    const meBtn = btn('orbat-c-btn', 'Deleger til meg', 'Sett meg som sjef for enheten');
    meBtn.disabled = !canEdit;
    meBtn.onclick = () => this.emit({ op: 'delegate', id: u.id, commanderId: this.hooks.user.id });
    this.detailEl.append(meBtn);

    this.renderKeySection(u, canEdit, field);

    // Oppsatt styrke
    const auth = document.createElement('input');
    auth.type = 'number'; auth.min = '0'; auth.className = 'orbat-c-input';
    auth.value = String(u.authorized ?? 0); auth.disabled = !canEdit;
    auth.onchange = () => this.update(u.id, { authorized: Math.max(0, parseInt(auth.value, 10) || 0) });
    field('Oppsatt styrke', auth);

    // Notat
    const note = document.createElement('textarea');
    note.className = 'orbat-c-input orbat-c-note'; note.rows = 2; note.disabled = !canEdit;
    note.value = u.note ?? '';
    note.onchange = () => this.update(u.id, { note: note.value.trim() });
    field('Notat', note);

    // Handlinger
    const acts = div('orbat-c-actions');
    const bChild = btn('orbat-c-btn', '＋ Under-enhet', 'Ctrl+Enter');
    bChild.disabled = !canEdit; bChild.onclick = () => this.addChild(u.id);
    const bSib = btn('orbat-c-btn', '＋ Søsken', 'Shift+Enter');
    bSib.disabled = !canEdit || !u.parentId; bSib.onclick = () => this.addSibling(u.id);
    const bDel = btn('orbat-c-btn danger', '🗑 Slett', 'Delete');
    bDel.disabled = !canEdit; bDel.onclick = () => this.remove(u.id);
    acts.append(bChild, bSib, bDel);
    this.detailEl.append(acts);
  }

  // ── Personell → SKADE-nøkkel ───────────────────────────────────────────────
  /** Tolk et personfelt: telefonnummer → utledet GNÅ-id, ellers tilgivende
   * Matrix-id. null når input ikke kan bli en adresse. */
  private personId(raw: string): string | null {
    const v = (raw ?? '').trim();
    if (!v) return null;
    const dom = this.hooks.defaultDomain ?? 'matrix.follodefence.com';
    const gna = gnaUserIdFromPhone(v, dom);
    if (gna) return gna;
    const norm = normalizeMatrixId(v, dom);
    return norm.startsWith('@') && norm.includes(':') ? norm : null;
  }

  /** Slektslinjen fra enheten selv opp til roten (til org-utledning). */
  private linjeFor(u: OrbatUnit): Array<{ id: string; echelon: OrbatEchelon }> {
    const byId = new Map(this.units.map((x) => [x.id, x]));
    const linje: Array<{ id: string; echelon: OrbatEchelon }> = [];
    let cur: OrbatUnit | undefined = u;
    while (cur && linje.length <= this.units.length) {
      linje.push({ id: cur.id, echelon: cur.echelon });
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }
    return linje;
  }

  /**
   * «Send nøkkel»: knytt en person til enheten og gi vedkommende SKADE-tilgang
   * i ETT trekk. Rolle + org-scope UTLEDES av plasseringen i treet
   * (orgFraOrbatLinje / foreslattRolleForEchelon) — operatøren skal aldri måtte
   * gjenta i et admin-skjema det ORBAT-treet allerede vet. Hubben håndhever
   * RBAC på utstedelsen (lagfører+, aldri høyere rolle enn egen, innen scope);
   * nøkkelen leveres som GNÅ 1:1 via Feltveksleren.
   */
  private renderKeySection(u: OrbatUnit, canEdit: boolean, field: (label: string, input: HTMLElement) => void) {
    if (!this.hooks.onIssueKey || !this.hooks.onSendKey) return;
    if (ROLE_RANK[this.hooks.user.role] < ROLE_RANK['lagfører']) return;

    this.detailEl.append(span('orbat-c-label orbat-c-keyhead', 'SKADE-tilgang (send nøkkel)'));

    const person = textInput('', canEdit);
    person.placeholder = 'Telefonnummer eller @bruker:domene';
    field('Person', person);

    // Rolle: foreslått = førerrollen på enhetens nivå; aldri over egen rolle.
    const roller = (Object.keys(ROLE_RANK) as Role[])
      .filter((r) => ROLE_RANK[r] <= ROLE_RANK[this.hooks.user.role]);
    const foreslatt = foreslattRolleForEchelon(u.echelon);
    const rolle = document.createElement('select');
    rolle.className = 'orbat-c-input'; rolle.disabled = !canEdit;
    for (const r of roller) {
      const o = document.createElement('option');
      o.value = r; o.textContent = r + (r === foreslatt ? ' (foreslått)' : '');
      if (r === (roller.includes(foreslatt) ? foreslatt : roller[roller.length - 1])) o.selected = true;
      rolle.append(o);
    }
    field('Rolle', rolle);

    const status = span('orbat-c-keystatus', '');
    const sendBtn = btn('orbat-c-btn', '🔑 Utsted + send nøkkel via GNÅ', 'Utsted engangsnøkkel scopet til enheten og send den 1:1');
    sendBtn.disabled = !canEdit;
    sendBtn.onclick = async () => {
      const mxid = this.personId(person.value);
      if (!mxid) { status.textContent = 'Ugyldig person — oppgi telefonnummer eller @id.'; status.classList.add('feil'); return; }
      status.classList.remove('feil');
      status.textContent = 'Utsteder nøkkel …';
      sendBtn.disabled = true;
      const org = orgFraOrbatLinje(this.linjeFor(u));
      const res = await this.hooks.onIssueKey!(rolle.value as Role, org);
      sendBtn.disabled = !canEdit;
      if ('error' in res) { status.textContent = `Utstedelse avvist: ${res.error}`; status.classList.add('feil'); return; }
      const gyldigTil = new Date(res.expiresAt).toLocaleString('nb-NO');
      this.hooks.onSendKey!(mxid, undefined,
        `SKADE-tilgang: du er satt opp på «${u.name}» som ${rolle.value}. ` +
        `Logg inn på ${window.location.origin} og bruk engangsnøkkelen: ${res.code} ` +
        `(gyldig til ${gyldigTil}, kan bare brukes én gang).`);
      status.textContent = `Nøkkel sendt til ${mxid} — rolle ${rolle.value}, scope ${org.team ?? org.troop ?? org.area ?? org.district}.`;
      person.value = '';
    };
    const wrap = div('orbat-c-actions');
    wrap.append(sendBtn);
    this.detailEl.append(wrap, status);
  }

  // ── Handlinger ─────────────────────────────────────────────────────────────
  private select(id: string) {
    this.selectedId = id;
    for (const r of Array.from(this.treeEl.children)) r.classList.remove('sel');
    const idx = this.flatIds.indexOf(id);
    if (idx >= 0) (this.treeEl.children[idx] as HTMLElement)?.classList.add('sel');
    this.renderDetail(this.editableIds());
  }

  private toggleCollapse(id: string) {
    if (this.collapsed.has(id)) this.collapsed.delete(id); else this.collapsed.add(id);
    this.render();
  }

  private canEditId(id: string): boolean { return this.editableIds().has(id); }

  private update(id: string, patch: Partial<OrbatUnit>) {
    if (!this.canEditId(id)) return;
    this.emit({ op: 'update', id, patch });
    this.select(id);
  }

  private genId(): string {
    return `u-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
  }

  private addChild(parentId: string) {
    if (!this.canEditId(parentId)) return;
    const parent = this.units.find((u) => u.id === parentId);
    if (!parent) return;
    const ech = orbatChildEchelon(parent.echelon) ?? parent.echelon;
    const id = this.genId();
    this.emit({ op: 'create', unit: { id, name: 'Ny enhet', echelon: ech, parentId, authorized: 0, sidc: defaultSidc(ech) } });
    if (!this.collapsed.has(parentId)) { /* allerede åpen */ } else this.collapsed.delete(parentId);
    this.render();
    this.select(id);
    this.focusName();
  }

  private addSibling(id: string) {
    const u = this.units.find((x) => x.id === id);
    if (!u || !u.parentId || !this.canEditId(u.parentId)) return;
    const nid = this.genId();
    this.emit({ op: 'create', unit: { id: nid, name: 'Ny enhet', echelon: u.echelon, parentId: u.parentId, authorized: 0, sidc: defaultSidc(u.echelon) } });
    this.select(nid);
    this.focusName();
  }

  private remove(id: string) {
    if (!this.canEditId(id)) return;
    const u = this.units.find((x) => x.id === id);
    if (!u) return;
    bekreft({
      tittel: `Slette «${u.name}» og hele undergrenen?`,
      tekst: 'Enheten og alt under den i treet fjernes fra ORBAT.',
      bekreftTekst: 'Slett',
      onBekreft: () => {
        this.selectedId = u.parentId ?? null;
        this.emit({ op: 'delete', id });
      },
    });
  }

  private reparent(srcId: string, newParentId: string) {
    if (!this.canEditId(srcId) || !this.canEditId(newParentId)) return;
    this.emit({ op: 'move', id: srcId, parentId: newParentId });
    this.select(srcId);
  }

  private focusName() {
    const inp = this.detailEl.querySelector<HTMLInputElement>('.orbat-c-nameinput');
    if (inp) { inp.focus(); inp.select(); }
  }

  // ── Tastatur ────────────────────────────────────────────────────────────────
  private onKey(e: KeyboardEvent) {
    const id = this.selectedId;
    if (e.key === 'ArrowDown') { e.preventDefault(); this.moveSel(1); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); this.moveSel(-1); return; }
    if (!id) return;
    const u = this.units.find((x) => x.id === id);
    if (!u) return;
    const hasChildren = this.units.some((x) => x.parentId === id);
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (hasChildren && this.collapsed.has(id)) this.toggleCollapse(id);
      else this.moveSel(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (hasChildren && !this.collapsed.has(id)) this.toggleCollapse(id);
      else if (u.parentId && this.flatIds.includes(u.parentId)) this.select(u.parentId);
    } else if (e.key === 'F2') {
      e.preventDefault(); this.focusName();
    } else if (e.key === 'Delete') {
      e.preventDefault(); this.remove(id);
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault(); this.addChild(id);
    } else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault(); this.addSibling(id);
    }
  }

  private moveSel(delta: number) {
    if (!this.flatIds.length) return;
    let idx = this.selectedId ? this.flatIds.indexOf(this.selectedId) : -1;
    idx = Math.max(0, Math.min(this.flatIds.length - 1, idx + delta));
    this.select(this.flatIds[idx]);
    (this.treeEl.children[idx] as HTMLElement)?.scrollIntoView({ block: 'nearest' });
  }
}

// ── DOM-hjelpere ─────────────────────────────────────────────────────────────
function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function btn(cls: string, text: string, title: string): HTMLButtonElement {
  const b = document.createElement('button'); b.className = cls; b.textContent = text; if (title) b.title = title; return b;
}
function textInput(value: string, enabled: boolean): HTMLInputElement {
  const i = document.createElement('input'); i.type = 'text'; i.className = 'orbat-c-input'; i.value = value; i.disabled = !enabled; return i;
}
